import React from 'react';
import {
  Box,
  ButtonBase,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ChevronRightRounded as ChevronRightIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import AskRecordAnswerText from './AskRecordAnswerText';
import AskRecordSourceList from './AskRecordSourceList';
import AskRecordThinkingState from './AskRecordThinkingState';
import {
  AskRecordCopyAction,
  AskRecordSummarySection,
} from './AskRecordShareActions';
import { getAskFollowUpSuggestions } from './askRecordFollowUpQuestion';
import {
  ASK_ACCENT,
  ASK_ACCENT_DARK,
  ASK_COLORS,
  formatConfidenceLabel,
} from './askRecordUi';

const AskRecordQuestionBubble = ({ lastQuestion }) => (
  lastQuestion ? (
    <Box sx={{ width: '100%', mb: 1.15, pt: 0.75, px: { xs: 0.75, sm: 1 } }}>
      <Typography sx={{ width: '100%', color: ASK_ACCENT_DARK, fontSize: { xs: '0.9rem', sm: '0.96rem' }, lineHeight: 1.26, fontWeight: 600 }}>
        {lastQuestion}
      </Typography>
    </Box>
  ) : null
);

const AskRecordTurn = ({ onOpenSource, turn }) => (
  <Box sx={{ mb: 1.4 }}>
    <AskRecordQuestionBubble lastQuestion={turn.question} />
    <Box sx={{ minWidth: 0, px: { xs: 0.75, sm: 1 } }}>
      <AskRecordAnswerText answer={turn.answer} sources={turn.sources || []} />
      <AskRecordCopyAction label="Copy answer" text={turn.answer} />
    </Box>
    <AskRecordSourceList onOpenSource={onOpenSource} sources={turn.sources || []} />
  </Box>
);

const AskRecordConversationResult = ({
  conversation,
  firstName,
  confidence,
  followUps,
  lastQuestion,
  loading,
  onAsk,
  onOpenSource,
  onCreateSummary,
  renderFollowUpForm,
  sources,
  summary,
  summaryError,
  summaryLoading,
}) => {
  if (loading) {
    return (
      <Box sx={{ position: 'relative', zIndex: 1, mt: 0.1 }}>
        {conversation.map((turn, index) => (
          <AskRecordTurn key={`${turn.question}-${index}`} onOpenSource={onOpenSource} turn={turn} />
        ))}
        <AskRecordQuestionBubble lastQuestion={lastQuestion} />
        <AskRecordThinkingState compact firstName={firstName} />
      </Box>
    );
  }

  const hasTaskFollowUps = Boolean(followUps?.length);
  const followUpSuggestions = hasTaskFollowUps
    ? followUps
    : getAskFollowUpSuggestions({ sources });
  const hasSources = sources.length > 0;

  return (
    <Box sx={{ position: 'relative', zIndex: 1, mt: 0 }}>
      {conversation.map((turn, index) => (
        <AskRecordTurn key={`${turn.question}-${index}`} onOpenSource={onOpenSource} turn={turn} />
      ))}

      {hasSources ? (
        <Box sx={{ mt: 1.1, pt: 0.85, borderTop: `1px dashed ${alpha(colors.brand.tint, 0.24)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.86, py: 0.38, borderRadius: '999px', bgcolor: alpha(colors.brand.tint, 0.12), color: colors.brand.deep, fontSize: '0.74rem', lineHeight: 1, fontWeight: 500, letterSpacing: '0.08em' }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '999px', bgcolor: alpha(colors.brand.deep, 0.55) }} />
            {formatConfidenceLabel(confidence)}
          </Typography>
          <Typography sx={{ fontSize: '0.86rem', lineHeight: 1, fontWeight: 500, color: ASK_ACCENT_DARK, display: 'inline-flex', alignItems: 'center', gap: 0.2 }}>
            {sources.length} source{sources.length === 1 ? '' : 's'} <ChevronRightIcon sx={{ fontSize: 16 }} />
          </Typography>
        </Box>
      ) : null}

      <AskRecordSummarySection
        createSummary={onCreateSummary}
        loading={summaryLoading}
        onOpenSource={onOpenSource}
        summary={summary}
        summaryError={summaryError}
      />

      <Typography sx={{ mt: 1.35, mb: 0.8, ml: { xs: 2.55, sm: 3.1 }, fontSize: '0.8rem', lineHeight: 1, fontWeight: 950, letterSpacing: '0.12em', color: alpha(colors.semantic.ink, 0.58) }}>
        FOLLOW UP
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.65, ml: { xs: 2.55, sm: 3.1 } }}>
        {followUpSuggestions.map((suggestion) => (
          <ButtonBase
            key={suggestion}
            onClick={() => onAsk(suggestion)}
            disabled={loading}
            sx={{
              px: 1.3,
              py: 0.62,
              borderRadius: '999px',
              bgcolor: ASK_COLORS.accentSoft,
              color: ASK_ACCENT_DARK,
              border: `1px solid ${alpha(ASK_ACCENT, 0.16)}`,
              maxWidth: '100%',
              '&:hover': {
                bgcolor: alpha(ASK_ACCENT, 0.18),
              },
            }}
          >
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 900, lineHeight: 1.12, textAlign: 'left' }}>
              {suggestion}
            </Typography>
          </ButtonBase>
        ))}
      </Box>

      <Box
        sx={{
          mt: 1.35,
          mx: { xs: -2, sm: -2.5 },
          px: { xs: 2, sm: 2.5 },
          pt: 1,
          pb: 0.25,
          borderTop: `1px solid ${alpha(colors.semantic.ink, 0.1)}`,
          bgcolor: ASK_COLORS.surface,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
        }}
      >
        {renderFollowUpForm()}
      </Box>
    </Box>
  );
};

export default AskRecordConversationResult;
